import {
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { useTheme } from "@mui/material/styles";
import ShiLogoUrl from "@images/shi-logo.svg";
import ShiLogoDarkUrl from "@images/shi-logo-dark.png";
import CxEmployeeAvatar, { AvatarSize } from "@components/CxEmployeeAvatar";
import type {
  AccountProfileShiTeamDto,
  YourShiAccountTeamMemberDto,
} from "@lib/ShiOneClient";

function memberKey(member: YourShiAccountTeamMemberDto, index: number) {
  return member.shiEmployeeId || member.customerFacingEmailAddress || member.fullName || String(index);
}

function ShiLogoHeader() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", py: 2 }}>
      <img
        src={isDark ? ShiLogoDarkUrl : ShiLogoUrl}
        style={{ height: 80, width: "auto", display: "block" }}
        alt="SHI Logo"
      />
    </Box>
  );
}

function ContactLine({ email, phoneNumber }: Readonly<{ email?: string | null; phoneNumber?: string | null }>) {
  return (
    <Stack spacing={0.75} sx={{ mt: 1 }}>
      {email && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <EmailIcon fontSize="small" color="action" />
          <Link href={`mailto:${email}`} variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {email.toLowerCase()}
          </Link>
        </Box>
      )}
      {phoneNumber && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <PhoneIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {phoneNumber}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

function TeamMemberCard({ member, featured = false }: Readonly<{ member: YourShiAccountTeamMemberDto; featured?: boolean }>) {
  const fullName = member.fullName || "SHI Team Member";

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 1,
        borderColor: featured ? "primary.main" : "divider",
        bgcolor: "background.paper",
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box display="flex" alignItems="center" gap={2}>
          <CxEmployeeAvatar
            entraId={member.shiEmployeeId ?? ""}
            displayName={fullName}
            photoSize={AvatarSize.xl}
          />
          <Box minWidth={0} flex={1}>
            <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
              <Typography variant="subtitle2" noWrap title={fullName} sx={{ fontWeight: 700 }}>
                {fullName}
              </Typography>
              {featured && <Chip size="small" color="primary" variant="outlined" label="Primary" />}
            </Stack>
            <Typography variant="body2" color="text.secondary" title={member.customerFacingTitle ?? undefined}>
              {member.customerFacingTitle || "Account Team"}
            </Typography>
            <ContactLine email={member.customerFacingEmailAddress} phoneNumber={member.phoneNumber} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function TeamSection({ title, subTitle, members, featured = false }: Readonly<{
  title: string;
  subTitle: string;
  members: YourShiAccountTeamMemberDto[];
  featured?: boolean;
}>) {
  if (!members.length) return null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 1, bgcolor: "background.paper", width: "100%" }}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {subTitle}
            </Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: featured ? "1fr" : "1fr 1fr" }, gap: 1.25 }}>
            {members.map((member, index) => (
              <TeamMemberCard key={memberKey(member, index)} member={member} featured={featured} />
            ))}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ShiAccountTeamTab({ team }: Readonly<{ team: AccountProfileShiTeamDto }>) {
  const primary = team.primaryAccountExecutive ? [team.primaryAccountExecutive] : [];
  const members = team.yourShiTeamMembers ?? [];

  if (!primary.length && !members.length) {
    return (
      <Stack spacing={2} sx={{ p: 3 }}>
        <ShiLogoHeader />
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Your Account Team is not yet assigned. Once assigned, your dedicated SHI account team members will be listed here.
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ p: 3 }}>
      <ShiLogoHeader />

      <TeamSection
        title="Account Executive"
        subTitle="Your primary point of contact at SHI"
        members={primary}
        featured
      />
      <TeamSection
        title="Account Team"
        subTitle="Specialized experts supporting your business needs"
        members={members}
      />
    </Stack>
  );
}
